import React, { Suspense, useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import LoadingScreen from '../../components/LoadingScreen';
import { setWallet } from '../../redux/actions/walletActions';
import { ADMIN_API } from '../../constants/routes';
import { deleteWallet } from '../../services/admin';

const ViewWalletComponent = React.lazy(() => import('./components/ViewWallet'));

const ViewWallet = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [walletData, setWalletData] = useState(null);

  const store = useSelector((state) => ({
    wallets: state.wallet
  }), shallowEqual);

  const walletFromStore = store.wallets.find((wallet) => id === wallet.id);

  useEffect(() => {
    const fetchWalletById = async () => {
      if (walletFromStore) {
        setWalletData(walletFromStore);
        return;
      }

      try {
        setLoading(true);
        const authToken = JSON.parse(localStorage.getItem(process.env.REACT_APP_ADMIN_AUTH_TOKEN));

        const requestOptions = {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken.token}`
          }
        };

        const response = await fetch(`${ADMIN_API}/wallets/${id}`, requestOptions);
        const result = await response.json();

        if (result?.data) {
          const fetchedWallet = result.data;
          const normalizedWallet = {
            id: fetchedWallet?.id,
            name: fetchedWallet?.name,
            acctNo: fetchedWallet?.account_number,
            bankName: fetchedWallet?.bank_name,
            currency: fetchedWallet?.currency,
            walletType: fetchedWallet?.type,
            balance: fetchedWallet?.balance,
            interestRate: fetchedWallet?.interest_rate,
            parentId: fetchedWallet?.parent_id,
            profileImage: fetchedWallet?.profile_image,
            provider: fetchedWallet?.provider,
            status: fetchedWallet?.status,
          };

          setWalletData(normalizedWallet);
          dispatch(setWallet([...store.wallets, normalizedWallet]));
        }
      } catch (error) {
        console.error('Error fetching wallet:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWalletById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, walletFromStore]);

  const handleDelete = async () => {
    try {
      const authToken = JSON.parse(localStorage.getItem(process.env.REACT_APP_ADMIN_AUTH_TOKEN));
      await deleteWallet(id, { token: authToken.token });
      enqueueSnackbar('Wallet deleted successfully', { variant: 'success' });
      navigate(-1);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Failed to delete wallet', { variant: 'error' });
    }
  };

  if (loading) {
    return <LoadingScreen isDashboard={pathname.includes('/dashboard')} />;
  }

  return (
    <>
      {walletData && (
        <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
          <ViewWalletComponent
            wallet={walletData}
            onDelete={handleDelete}
          />
        </Suspense>
      )}
    </>
  );
}

export default ViewWallet;